import React from "react";
import { Link } from "react-router-dom";
import CaseStudyCard from "../components/CaseStudyCard";
import "../assets/css/CaseStudies.css";

/* =========================================================
   SITE CONFIG
========================================================= */

const SITE_URL = "https://hexxatech.co.in";
const PAGE_URL = `${SITE_URL}/case-studies`;

const WHATSAPP_URL =
  "https://api.whatsapp.com/send/?phone=919226591664&text=Hello+HexxaTech%2C+I+would+like+to+discuss+a+software+project.&type=phone_number&app_absent=0";

/* =========================================================
   CASE STUDIES
========================================================= */

const studies = [
  {
    slug: "cartattva",
    title: "CarTattva",
    subtitle: "Vehicle Inspection & Business Management Platform",
    industry: "Automotive",
    description:
      "A custom vehicle inspection and business management platform developed to manage customers, dealers, leads, appointments, evaluator operations, inspections and automated reporting through a connected digital workflow.",
    highlights: [
      "Customer & Dealer Management",
      "Lead & Follow-up Tracking",
      "Appointment Scheduling",
      "Vehicle Inspection Management",
      "Evaluator Operations",
      "Offline Mobile App",
      "Automated Reporting",
    ],
  },

  {
    slug: "swasthya",
    title: "Swasthya",
    subtitle: "Hospital & Clinic Management System",
    industry: "Healthcare",
    description:
      "A customized hospital and clinic management solution designed to help healthcare organizations manage patient records, appointments, treatment sessions, medical information, billing and day-to-day clinical operations.",
    highlights: [
      "Patient Management",
      "Appointment Management",
      "Medical Records",
      "Treatment Management",
      "Billing & Reports",
      "Clinic Operations",
    ],
  },

  {
    slug: "wisetech",
    title: "WiseTech License Management",
    subtitle: "Online Software License Management Platform",
    industry: "Industrial Software",
    description:
      "A centralized software license management platform developed to manage client registrations, software products, license information and license validation with real-time visibility into software license status.",
    highlights: [
      "Client & Software Registration",
      "License Management",
      "License Validation",
      "License Status Tracking",
      "Software Registration",
      "Centralized Administration",
    ],
  },

  {
    slug: "inventory-management",
    title: "Inventory & Store Management System",
    subtitle:
      "Custom Inventory & Store Room Software for Infino Media",
    industry: "Inventory & Store Management",
    description:
      "A customized inventory and store room management solution developed for Infino Media to manage stock, employee material requests, material issue and return, purchase orders, material inward, delivery challans, CRI, CRO and inventory transactions through a centralized platform.",
    highlights: [
      "Store Room Stock Management",
      "Employee Material Requests",
      "Material Issue & Return",
      "Purchase Order Management",
      "Material Inward",
      "Delivery Challan Management",
      "CRI & CRO Management",
      "Inventory Reports",
    ],
  },

  {
    slug: "process-control",
    title: "Process Control System",
    subtitle: "Industrial Process Monitoring Software",
    industry: "Manufacturing",
    description:
      "An integrated manufacturing and process control platform connecting quotations, orders, BOM, process vouchers, production workflows, finished goods, invoicing and payment receipts into a centralized business system.",
    highlights: [
      "Quotation to Order Workflow",
      "BOM & Process Management",
      "Process Voucher Tracking",
      "Production Workflow",
      "Finished Goods Management",
      "Invoice & Proforma Invoice",
      "Payment Receipt Tracking",
    ],
  },

  {
    slug: "satellites",
    title: "Satellite Operations",
    subtitle: "Satellite Operations Management System",
    industry: "Aerospace & Space",
    description:
      "A comprehensive satellite operations management platform designed to support satellite assets, ground station operations, telemetry monitoring, mission planning, scheduling, anomaly detection and orbital operations through an integrated command and control workflow.",
    highlights: [
      "Satellite Asset Management",
      "Real-time Telemetry Monitoring",
      "Ground Station Operations",
      "Mission Planning & Scheduling",
      "Anomaly Detection & Alerts",
      "Maintenance & Service Tracking",
    ],
  },
];

/* =========================================================
   STATS
========================================================= */

const stats = [
  {
    value: "20+",
    label: "Projects Delivered",
  },
  {
    value: "10+",
    label: "Industries Served",
  },
  {
    value: "100%",
    label: "Custom Built Solutions",
  },
];

/* =========================================================
   INDUSTRY ICONS
========================================================= */

const industryIcons = {
  Automotive: "🚗",
  Healthcare: "🏥",
  "Industrial Software": "⚙️",
  "Inventory & Store Management": "📦",
  Manufacturing: "🏭",
  "Aerospace & Space": "🛰️",
};

/* =========================================================
   COMPONENT
========================================================= */

export default function CaseStudiesIndex() {
  /* =======================================================
     STRUCTURED DATA
  ======================================================= */

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      /* ---------------------------------------------------
         ORGANIZATION
      --------------------------------------------------- */

      {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: "HexxaTech Softwares",
        url: SITE_URL,
        description:
          "HexxaTech Softwares is a custom software development company in Pune, Maharashtra, building ERP, CRM, business automation, web applications and mobile applications for businesses.",
        areaServed: {
          "@type": "City",
          name: "Pune",
        },
        address: {
          "@type": "PostalAddress",
          addressLocality: "Pune",
          addressRegion: "Maharashtra",
          addressCountry: "IN",
        },
        knowsAbout: [
          "Custom Software Development",
          "ERP Development",
          "CRM Development",
          "Business Automation",
          "Web Application Development",
          "Mobile Application Development",
          "Inventory Management Software",
          "Healthcare Software",
          "Manufacturing Software",
          "Industrial Software",
        ],
      },

      /* ---------------------------------------------------
         WEBSITE
      --------------------------------------------------- */

      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        url: SITE_URL,
        name: "HexxaTech Softwares",
        publisher: {
          "@id": `${SITE_URL}/#organization`,
        },
      },

      /* ---------------------------------------------------
         COLLECTION PAGE
      --------------------------------------------------- */

      {
        "@type": "CollectionPage",
        "@id": `${PAGE_URL}#webpage`,
        url: PAGE_URL,
        name:
          "Custom Software Development Case Studies in Pune | HexxaTech Softwares",
        description:
          "Explore custom software development case studies by HexxaTech Softwares, including ERP, CRM, healthcare, automotive, inventory, manufacturing, industrial and aerospace software solutions.",
        isPartOf: {
          "@id": `${SITE_URL}/#website`,
        },
        about: {
          "@type": "Thing",
          name: "Custom Software Development",
        },
        publisher: {
          "@id": `${SITE_URL}/#organization`,
        },
        inLanguage: "en-IN",
      },

      /* ---------------------------------------------------
         ITEM LIST
      --------------------------------------------------- */

      {
        "@type": "ItemList",
        "@id": `${PAGE_URL}#case-studies`,
        name: "HexxaTech Software Development Case Studies",
        numberOfItems: studies.length,
        itemListOrder: "https://schema.org/ItemListOrderAscending",
        itemListElement: studies.map((study, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: study.title,
          url: `${PAGE_URL}/${study.slug}`,
          item: {
            "@type": "CreativeWork",
            name: study.title,
            description: study.description,
            url: `${PAGE_URL}/${study.slug}`,
          },
        })),
      },

      /* ---------------------------------------------------
         BREADCRUMBS
      --------------------------------------------------- */

      {
        "@type": "BreadcrumbList",
        "@id": `${PAGE_URL}#breadcrumb`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: `${SITE_URL}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Case Studies",
            item: PAGE_URL,
          },
        ],
      },
    ],
  };

  return (
    <>
      {/* =====================================================
          SEO
      ====================================================== */}

      <title>
        Custom Software Development Case Studies in Pune | HexxaTech
      </title>

      <meta
        name="description"
        content="Explore real-world custom software development case studies by HexxaTech Softwares in Pune. See ERP, CRM, healthcare, inventory, manufacturing, industrial and mobile software projects."
      />

      <meta
        name="keywords"
        content="custom software development Pune, software development company Pune, custom software company Pune, ERP development Pune, CRM development Pune, business automation Pune, software development case studies, custom ERP software, custom CRM software"
      />

      <meta
        name="robots"
        content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      />

      <meta name="author" content="HexxaTech Softwares" />

      <meta name="language" content="English" />

      <meta name="geo.region" content="IN-MH" />
      <meta name="geo.placename" content="Pune" />

      <link rel="canonical" href={PAGE_URL} />

      {/* =====================================================
          OPEN GRAPH
      ====================================================== */}

      <meta
        property="og:title"
        content="Custom Software Development Case Studies | HexxaTech"
      />

      <meta
        property="og:description"
        content="Explore real-world ERP, CRM, healthcare, inventory, manufacturing, industrial and business automation software projects developed by HexxaTech Softwares."
      />

      <meta property="og:url" content={PAGE_URL} />

      <meta property="og:type" content="website" />

      <meta property="og:site_name" content="HexxaTech Softwares" />

      <meta property="og:locale" content="en_IN" />

      {/* =====================================================
          TWITTER / X
      ====================================================== */}

      <meta name="twitter:card" content="summary_large_image" />

      <meta
        name="twitter:title"
        content="Custom Software Development Case Studies | HexxaTech"
      />

      <meta
        name="twitter:description"
        content="Explore custom software projects developed by HexxaTech Softwares across automotive, healthcare, inventory, manufacturing, industrial software and aerospace."
      />

      {/* =====================================================
          STRUCTURED DATA
      ====================================================== */}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(structuredData),
        }}
      />

      {/* =====================================================
          PAGE
      ====================================================== */}

      <main className="case-studies-page">

        {/* =================================================
            HERO
        ================================================== */}

        <section
          className="case-hero"
          aria-labelledby="case-studies-page-title"
        >
          <div
            className="case-hero-glow case-hero-glow-one"
            aria-hidden="true"
          />

          <div
            className="case-hero-glow case-hero-glow-two"
            aria-hidden="true"
          />

          <div className="case-container">

            {/* Breadcrumb */}

            <nav
              className="case-breadcrumb"
              aria-label="Breadcrumb"
            >
              <Link to="/">Home</Link>

              <span aria-hidden="true">/</span>

              <span aria-current="page">
                Case Studies
              </span>
            </nav>

            {/* Hero Content */}

            <div className="case-hero-content">

              <span className="case-eyebrow">
                <span
                  className="eyebrow-dot"
                  aria-hidden="true"
                />

                HexxaTech Portfolio
              </span>

              <h1 id="case-studies-page-title">
                Custom Software Development
                <br />

                <span>
                  Case Studies
                </span>
              </h1>

              <p>
                Explore real-world software projects developed
                by HexxaTech Softwares for businesses in Pune
                and across different industries. From ERP and
                CRM platforms to healthcare, automotive,
                inventory, manufacturing and specialized
                business applications, our solutions are built
                around real operational workflows.
              </p>

              {/* Capability Keywords */}

              <div
                className="hero-keywords"
                aria-label="Software development capabilities"
              >
                <span>Custom Software</span>
                <span>ERP & CRM</span>
                <span>Business Automation</span>
                <span>Web Applications</span>
                <span>Mobile Applications</span>
              </div>

              {/* CTA */}

              <div className="hero-actions">

                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="primary-button"
                  aria-label="Discuss your software project with HexxaTech on WhatsApp"
                >
                  Discuss Your Project

                  <span aria-hidden="true">
                    →
                  </span>
                </a>

                <a
                  href="#case-studies"
                  className="secondary-button"
                >
                  Explore Our Work

                  <span aria-hidden="true">
                    ↓
                  </span>
                </a>

              </div>

            </div>
          </div>
        </section>

        {/* =================================================
            STATS
        ================================================== */}

        <section
          className="stats-section"
          aria-label="HexxaTech project statistics"
        >
          <div className="case-container">

            <div className="stats-card">

              {stats.map((item, index) => (
                <div
                  className="stat-item"
                  key={item.label}
                >
                  <strong>
                    {item.value}
                  </strong>

                  <span>
                    {item.label}
                  </span>

                  {index !== stats.length - 1 && (
                    <div
                      className="stat-divider"
                      aria-hidden="true"
                    />
                  )}
                </div>
              ))}

            </div>

          </div>
        </section>

        {/* =================================================
            INTRODUCTION
        ================================================== */}

        <section
          className="portfolio-intro"
          aria-labelledby="portfolio-intro-heading"
        >
          <div className="case-container">

            <div className="intro-grid">

              <div>

                <span className="section-label">
                  Selected Work
                </span>

                <h2 id="portfolio-intro-heading">
                  Software built around
                  <br />
                  the way businesses work.
                </h2>

              </div>

              <div>

                <p>
                  Every business has different processes,
                  challenges and operational requirements.
                  HexxaTech develops custom software around
                  those workflows instead of forcing businesses
                  to adapt to generic software.
                </p>

                <p className="intro-highlight">
                  Our portfolio includes healthcare,
                  automotive, inventory, manufacturing,
                  industrial software and aerospace
                  solutions.
                </p>

              </div>

            </div>

          </div>
        </section>

        {/* =================================================
            CASE STUDIES
        ================================================== */}

        <section
          id="case-studies"
          className="case-studies-section"
          aria-labelledby="case-studies-heading"
        >
          <div className="case-container">

            <div className="section-heading">

              <div>

                <span className="section-label">
                  Our Projects
                </span>

                <h2 id="case-studies-heading">
                  Real solutions.
                  <br />
                  Real business workflows.
                </h2>

              </div>

              <p>
                Explore selected custom software projects
                developed to solve specific operational,
                workflow and business challenges.
              </p>

            </div>

            {/* Case Study Cards */}

            <div className="case-study-grid">

              {studies.map((study) => (
                <article
                  key={study.slug}
                  className="case-study-item"
                >
                  <CaseStudyCard
                    slug={study.slug}
                    title={study.title}
                    subtitle={study.subtitle}
                    industry={study.industry}
                    description={study.description}
                    highlights={study.highlights}
                  />
                </article>
              ))}

            </div>

            {/* Internal Linking */}

            <div className="case-study-internal-links">

              <p>
                Looking for a specific type of software?
              </p>

              <div className="internal-link-list">

                <Link to="/services/custom-software-development">
                  Custom Software Development
                </Link>

                <Link to="/services/erp-business-management">
                  ERP & Business Management
                </Link>

                <Link to="/services/crm-lead-management">
                  CRM & Lead Management
                </Link>

                <Link to="/services/healthcare-software">
                  Healthcare Software
                </Link>

                <Link to="/services/inventory-management">
                  Inventory Management
                </Link>

                <Link to="/services/mobile-app-development">
                  Mobile App Development
                </Link>

                <Link to="/services/industrial-process-software">
                  Industrial Process Software
                </Link>

                <Link to="/#contact">
                  Contact HexxaTech
                </Link>

              </div>

            </div>

          </div>
        </section>

        {/* =================================================
            INDUSTRIES
        ================================================== */}

        <section
          className="industries-section"
          aria-labelledby="industries-heading"
        >
          <div className="case-container">

            <div className="industries-card">

              <div className="industries-content">

                <span className="section-label">
                  Industries
                </span>

                <h2 id="industries-heading">
                  Technology that adapts
                  <br />
                  to your industry.
                </h2>

                <p>
                  Our software development experience spans
                  multiple industries. We understand that
                  business processes differ from one
                  organization to another, which is why our
                  solutions are designed around actual
                  operational requirements.
                </p>

              </div>

              <div className="industry-list">

                {[
                  "Automotive",
                  "Healthcare",
                  "Manufacturing",
                  "Industrial Software",
                  "Inventory & Store Management",
                  "Aerospace & Space",
                ].map((industry) => (
                  <div
                    className="industry-list-item"
                    key={industry}
                  >
                    <span
                      className="industry-icon"
                      aria-hidden="true"
                    >
                      {industryIcons[industry] || "💼"}
                    </span>

                    <strong>
                      {industry}
                    </strong>

                    <span
                      className="industry-arrow"
                      aria-hidden="true"
                    >
                      →
                    </span>
                  </div>
                ))}

              </div>

            </div>

          </div>
        </section>

        {/* =================================================
            PUNE / LOCAL SEO
        ================================================== */}

        <section
          className="portfolio-intro"
          aria-labelledby="pune-software-heading"
        >
          <div className="case-container">

            <div className="intro-grid">

              <div>

                <span className="section-label">
                  Pune Software Development
                </span>

                <h2 id="pune-software-heading">
                  Custom software development
                  <br />
                  for businesses in Pune.
                </h2>

              </div>

              <div>

                <p>
                  HexxaTech Softwares provides custom software
                  development services for businesses in Pune,
                  Maharashtra and clients beyond Pune. We
                  develop business applications around
                  operational workflows, including ERP,
                  CRM, inventory management, healthcare
                  software, manufacturing systems and
                  industry-specific platforms.
                </p>

                <p className="intro-highlight">
                  Have a business process that doesn't fit
                  standard software? We can design a solution
                  around your workflow.
                </p>

              </div>

            </div>

          </div>
        </section>

        {/* =================================================
            CAPABILITIES
        ================================================== */}

        <section
          className="capabilities-section"
          aria-labelledby="capabilities-heading"
        >
          <div className="case-container">

            <div className="section-heading centered">

              <span className="section-label">
                What We Build
              </span>

              <h2 id="capabilities-heading">
                From business idea to
                <br />
                working software.
              </h2>

              <p>
                We design and develop software solutions that
                fit your processes, integrate with existing
                systems and provide a foundation for future
                business growth.
              </p>

            </div>

            <div className="capabilities-grid">

              <div className="capability-card">
                <span>01</span>

                <h3>
                  Custom ERP
                </h3>

                <p>
                  Centralize business operations, workflows,
                  transactions, reporting and process
                  management with ERP software designed
                  around your organization.
                </p>

                <Link to="/services/erp-business-management">
                  Explore ERP & Business Management →
                </Link>
              </div>

              <div className="capability-card">
                <span>02</span>

                <h3>
                  CRM & Business Software
                </h3>

                <p>
                  Manage customers, leads, follow-ups,
                  sales processes and business relationships
                  through connected business software.
                </p>

                <Link to="/services/crm-lead-management">
                  Explore CRM & Lead Management →
                </Link>
              </div>

              <div className="capability-card">
                <span>03</span>

                <h3>
                  Custom Software Development
                </h3>

                <p>
                  Build secure and scalable software
                  tailored to your organization's workflows,
                  users and operational requirements.
                </p>

                <Link to="/services/custom-software-development">
                  Explore Custom Software Development →
                </Link>

                <Link to="/services/mobile-app-development">
                  Explore Mobile Development →
                </Link>
              </div>

              <div className="capability-card">
                <span>05</span>

                <h3>
                  Process Automation
                </h3>

                <p>
                  Replace repetitive manual processes with
                  connected digital workflows that improve
                  operational visibility and efficiency.
                </p>

                <Link to="/services/business-automation">
                  Explore Business Automation →
                </Link>
              </div>

              <div className="capability-card">
                <span>06</span>

                <h3>
                  Industry-specific Platforms
                </h3>

                <p>
                  Build specialized software for unique
                  operational requirements across healthcare,
                  manufacturing, automotive and other
                  industries.
                </p>

                <Link to="/services/custom-software-development">
                  Explore Custom Software →
                </Link>
              </div>

            </div>

          </div>
        </section>

        {/* =================================================
            RELATED PAGES
        ================================================== */}

        <section
          className="portfolio-intro"
          aria-labelledby="related-pages-heading"
        >
          <div className="case-container">

            <div className="section-heading">

              <div>

                <span className="section-label">
                  Explore HexxaTech
                </span>

                <h2 id="related-pages-heading">
                  More about our software
                  <br />
                  development services.
                </h2>

              </div>

              <p>
                Learn more about our development capabilities,
                industries and approach to building custom
                business software.
              </p>

            </div>

            <div className="capabilities-grid">

              <div className="capability-card">

                <span>01</span>

                <h3>
                  Custom Software Development
                </h3>

                <p>
                  Software designed around your business
                  requirements instead of generic workflows.
                </p>

                <Link to="/services/custom-software-development">
                  Learn More →
                </Link>

              </div>

              <div className="capability-card">

                <span>02</span>

                <h3>
                  Software Development Company in Pune
                </h3>

                <p>
                  Explore HexxaTech's software development
                  capabilities for businesses in Pune and
                  beyond.
                </p>

                <Link to="/">
                  Visit HexxaTech →
                </Link>

              </div>

              <div className="capability-card">

                <span>03</span>

                <h3>
                  Contact Our Team
                </h3>

                <p>
                  Discuss your business process, software
                  requirements or automation idea with our
                  development team.
                </p>

                <Link to="/contact">
                  Contact Us →
                </Link>

              </div>

            </div>

          </div>
        </section>

        {/* =================================================
            CTA
        ================================================== */}

        <section
          className="case-cta"
          aria-labelledby="case-cta-heading"
        >

          <div
            className="cta-glow"
            aria-hidden="true"
          />

          <div className="case-container">

            <div className="cta-inner">

              <span className="cta-badge">
                Start Your Project
              </span>

              <h2 id="case-cta-heading">
                Have a business problem
                <br />
                that software can solve?
              </h2>

              <p>
                Tell us about your workflow, challenges and
                goals. We'll help you turn them into a
                practical, scalable software solution built
                specifically for your business.
              </p>

              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="primary-button"
                aria-label="Discuss your software project with HexxaTech on WhatsApp"
              >
                Discuss Your Project

                <span aria-hidden="true">
                  →
                </span>
              </a>

            </div>

          </div>

        </section>

      </main>
    </>
  );
}